import styled from "@emotion/native"
import { RadioButton, Spacing, Text } from "../ui-components"
import CloseIcon from "../assets/close-icon.svg"
import { useTheme } from "@emotion/react"
import { useAccountList } from "../api/queries"
import { ListItem } from "../ui-components/ListItem"
import { Platform, ScrollView } from "react-native"
import { AccountBottomSheetAddAccountButton } from "./AccountBottomSheetAddAccountButton"

export type AccountBottomSheetInnerContentProps = {
  handleAccountSelect: (accountId: string) => void
  selectedAccountId?: string
  onClose: () => void
  scrollable?: boolean
  /** When true, the parent renders the add-account action (e.g. TrueSheet footer). */
  hideAddAccountButton?: boolean
}

export function AccountBottomSheetInnerContent({
  handleAccountSelect,
  selectedAccountId,
  onClose,
  scrollable: scrollableProp = false,
  hideAddAccountButton = false,
}: AccountBottomSheetInnerContentProps) {
  const { data } = useAccountList()
  const theme = useTheme()
  const scrollable = scrollableProp && Platform.OS !== "android"

  const addAccountButton = hideAddAccountButton ? null : (
    <>
      <Spacing top={16} />
      <AccountBottomSheetAddAccountButton />
    </>
  )

  const accountList = (
    <ItemsContainer>
      {data?.accounts.map((acc) => (
        <AccountRow key={acc.id}>
          <ListItem
            name={acc.name}
            description={`Account ending in ${acc.accountMask}`}
            onPress={() => {
              handleAccountSelect(acc.id)
              onClose()
            }}
            rightElement={
              <RadioButton
                fillColor={theme.paymentRequestScreen.radioButtonColor}
                checked={selectedAccountId === acc.id}
                handleCheck={() => {
                  handleAccountSelect(acc.id)
                  onClose()
                }}
              />
            }
          />
        </AccountRow>
      ))}
      {addAccountButton}
    </ItemsContainer>
  )

  return (
    <SheetChrome>
      <BottomSheetViewContainer style={scrollable ? { flex: 1 } : undefined}>
        <BottomSheetContent style={scrollable ? { flex: 1 } : undefined}>
          <UpperControlsContainer>
            <HeaderTitle type="h2" fontFamily="IBMPlexSans-Medium" numberOfLines={1}>
              Switch account
            </HeaderTitle>
            <CloseIconContainer onPress={onClose}>
              <CloseIcon color="black" />
            </CloseIconContainer>
          </UpperControlsContainer>
          <Spacing top={16} />
          {scrollable ? (
            <ScrollView
              style={{ flex: 1 }}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={{ paddingBottom: 12 }}
            >
              {accountList}
            </ScrollView>
          ) : (
            accountList
          )}
        </BottomSheetContent>
      </BottomSheetViewContainer>
    </SheetChrome>
  )
}

const SheetChrome = styled.View`
  margin: 0 16px 0 16px;
`

const CloseIconContainer = styled.Pressable`
  width: 40px;
  height: 40px;
  border-radius: 100px;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.filtersSheet.closeContainerColor};
`

const BottomSheetViewContainer = styled.View`
  padding: 16px 0 0 0;
  align-items: center;
`

const BottomSheetContent = styled.View`
  margin: 0 28px 0 28px;
  width: 100%;
`

const ItemsContainer = styled.View`
  width: 100%;
  gap: 12px;
  padding-bottom: 12px;
`

const AccountRow = styled.View`
  width: 100%;
  min-height: 64px;
  border-radius: 20px;
  background-color: #ffffff;
  padding: 8px 16px;
  justify-content: center;
`

const UpperControlsContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  min-height: 56px;
  padding-bottom: 16px;
  border-bottom-width: 1px;
  border-bottom-color: ${({ theme }) => theme.filtersSheet.headerSeparatorColor};
`

const HeaderTitle = styled(Text)`
  flex: 1;
  margin-right: 12px;
`
